import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Clock, ChevronRight, CalendarDays, AlertCircle, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: "today", label: "Today" },
  { key: "upcoming", label: "Upcoming" },
  { key: "completed", label: "Completed" },
];

export const TeamJobs = () => {
  const [tab, setTab] = useState("today");
  const [data, setData] = useState({ today: [], upcoming: [], completed: [] });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/team/my/dashboard`);
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load jobs");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const rows = data[tab] || [];

  return (
    <div data-testid="team-jobs-page">
      <div>
        <h1 className="font-display font-black uppercase text-2xl sm:text-3xl text-[#111619]">My Jobs</h1>
        <p className="text-sm text-[#3f4b52]">Fitments assigned to you</p>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-1 bg-white border border-[#cfd7db] rounded-md p-1" data-testid="team-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
                  data-testid={`team-tab-${t.key}`}
                  className={`py-2 text-xs font-display uppercase tracking-widest rounded transition ${tab === t.key ? "bg-[#f28a16] text-white" : "text-[#3f4b52] hover:bg-[#f5f6f6]"}`}>
            {t.label}
            <span className="ml-1.5 text-xs opacity-70">({(data[t.key] || []).length})</span>
          </button>
        ))}
      </div>

      <div className="mt-5 space-y-3">
        {loading && <div className="text-sm text-[#3f4b52]">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="bg-white border border-dashed border-[#cfd7db] rounded-md p-8 text-center" data-testid={`team-empty-${tab}`}>
            {tab === "completed" ? <CheckCircle2 size={28} className="mx-auto text-[#9aa6ad] mb-2" /> : <AlertCircle size={28} className="mx-auto text-[#9aa6ad] mb-2" />}
            <p className="text-sm text-[#3f4b52]">
              {tab === "today" && "No fitments scheduled for today."}
              {tab === "upcoming" && "No upcoming fitments assigned to you."}
              {tab === "completed" && "No completed jobs to review yet."}
            </p>
          </div>
        )}
        {rows.map((j) => <JobCard key={j.id} job={j} />)}
      </div>
    </div>
  );
};

const STATUS_META = {
  new:                          { color: "bg-slate-100 text-slate-700",   label: "Scheduled" },
  scheduled:                    { color: "bg-slate-100 text-slate-700",   label: "Scheduled" },
  en_route:                     { color: "bg-amber-100 text-amber-800",   label: "En route" },
  on_site:                      { color: "bg-blue-100 text-blue-800",     label: "On site" },
  in_progress:                  { color: "bg-indigo-100 text-indigo-800", label: "In progress" },
  issue_reported:               { color: "bg-red-100 text-red-700",       label: "Issue" },
  completed_pending_review:     { color: "bg-emerald-100 text-emerald-800", label: "Ready to submit" },
  completed:                    { color: "bg-emerald-100 text-emerald-800", label: "Completed" },
};

const JobCard = ({ job }) => {
  const nav = useNavigate();
  const meta = STATUS_META[job.status] || STATUS_META.new;
  return (
    <div role="button" tabIndex={0}
         onClick={() => nav(`/team/jobs/${job.id}`)}
         onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && nav(`/team/jobs/${job.id}`)}
         data-testid={`team-job-${job.id}`}
         className="block bg-white border border-[#cfd7db] rounded-md p-4 shadow-sm hover:border-[#f28a16] transition cursor-pointer">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs text-[#3f4b52] font-display uppercase tracking-widest">
            Job #{job.job_number} · {job.quotation_number || "—"}
          </div>
          <div className="font-display font-bold text-lg text-[#111619] leading-tight truncate">
            {job.client_name || "Client"}
          </div>
          <div className="text-sm text-[#3f4b52] truncate">
            {job.vehicle_make_model || "—"} · <span className="font-semibold text-[#111619]">{job.registration || "—"}</span>
          </div>
        </div>
        <div className="text-right shrink-0 space-y-1">
          <div className="inline-flex items-center gap-1 text-[#f28a16] font-display font-black text-base">
            <Clock size={14} /> {job.fitment_time || "—"}
          </div>
          <div className="text-xs text-[#3f4b52]">{job.fitment_date || ""}</div>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2 flex-wrap text-xs text-[#3f4b52]">
        {job.fitment_address && (
          <span className="inline-flex items-center gap-1"><MapPin size={12} className="text-[#f28a16]" /> {job.fitment_address.length > 40 ? job.fitment_address.slice(0, 40) + "…" : job.fitment_address}</span>
        )}
        {job.contact_mobile && (
          <span className="inline-flex items-center gap-1"><Phone size={12} className="text-[#f28a16]" /> {job.contact_mobile}</span>
        )}
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm font-display uppercase tracking-widest text-xs ${meta.color}`}
              data-testid={`team-status-${job.id}`}>
          {meta.label}
        </span>
      </div>
      <div className="mt-3 inline-flex items-center gap-1 text-[#f28a16] font-display uppercase tracking-widest text-xs">
        Open job <ChevronRight size={14} />
      </div>
    </div>
  );
};

export default TeamJobs;
